/**
 * Application Configuration Module
 *
 * Centralizes application-wide configuration settings using environment variables
 * and provides fallback values for development and production environments.
 */

/**
 * API configuration settings
 * @typedef {Object} ApiConfig
 * @property {string} baseUrl - Base URL for API requests
 * @property {number} timeout - Request timeout in milliseconds
 * @property {number} retryAttempts - Number of retry attempts for failed requests
 */

/**
 * Feature flags configuration
 * @typedef {Object} FeatureConfig
 * @property {boolean} enableClustering - Enable mine clustering on map
 * @property {boolean} enableSearch - Enable search functionality
 * @property {boolean} enableFilters - Enable filtering functionality
 */

/**
 * EmailJS configuration settings
 * @typedef {Object} EmailConfig
 * @property {string} serviceId - EmailJS service ID
 * @property {string} templateId - EmailJS template ID
 * @property {string} publicKey - EmailJS public key
 */

/**
 * Data loading configuration
 * @typedef {Object} DataConfig
 * @property {number} batchSize - Batch size for data loading operations
 */

/**
 * Determines the API base URL based on environment and runtime context
 * @returns {string} The appropriate API base URL
 */
function getApiBaseUrl() {
  // Use explicit environment variable if set
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Auto-detect based on hostname
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isDevelopment ? 'http://localhost:4001/api' : '/api';
}

/**
 * Application configuration object
 * Contains all application-wide settings organized by category
 */
export const appConfig = {
  /**
   * API configuration settings
   * @type {ApiConfig}
   */
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: 10000, // 10 second timeout (moved from apiClient.js)
    retryAttempts: 3,
    rateLimitDelay: 300, // 300ms debounce delay (moved from filterManager.js)
    retryMaxDelay: 10000 // Maximum delay for retry backoff (moved from retryManager.js)
  },

  /**
   * Search configuration settings
   * @type {Object}
   */
  search: {
    debounceDelay: 500 // 500ms debounce for search suggestions
  },

  /**
   * Cache configuration settings
   * @type {Object}
   */
  cache: {
    ttl: 900000, // 15 minutes TTL (moved from dataService.js)
    maxKeys: 1000 // Maximum number of cache entries
  },

  /**
   * Feature flags for enabling/disabling application features
   * @type {FeatureConfig}
   */
  features: {
    enableClustering: false,
    enableSearch: true,
    enableFilters: true
  },

  /**
   * EmailJS configuration for contact form functionality
   * @type {EmailConfig}
   */
  email: {
    serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID,
    templateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
    publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY
  },

  /**
   * Data loading and processing configuration
   * @type {DataConfig}
   */
  data: {
    batchSize: parseInt(import.meta.env.VITE_BATCH_SIZE) || 1000
  },

  /**
   * UI component configuration
   * @type {Object}
   */
  ui: {
    welcomeSplash: {
      autoDismissDelay: 30000, // 30 seconds
      storageKey: 'globalminingrisk-splash-dismissed',
      enableKeyboardDismiss: true
    }
  }
};

/**
 * Validates that all required configuration values are present
 * @throws {Error} If required configuration is missing
 */
export function validateAppConfig() {
  const requiredEnvVars = [
    'VITE_MAPBOX_TOKEN',
    'VITE_EMAILJS_SERVICE_ID',
    'VITE_EMAILJS_TEMPLATE_ID',
    'VITE_EMAILJS_PUBLIC_KEY'
  ];

  const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);

  if (missingVars.length > 0) {
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
  }
}

/**
 * Gets the current environment (development/production)
 * @returns {string} The current environment
 */
export function getEnvironment() {
  return import.meta.env.MODE || 'development';
}

/**
 * Checks if the application is running in development mode
 * @returns {boolean} True if in development mode
 */
export function isDevelopment() {
  return import.meta.env.DEV === true;
}

/**
 * Checks if the application is running in production mode
 * @returns {boolean} True if in production mode
 */
export function isProduction() {
  return import.meta.env.PROD === true;
}